import { useMemo } from 'react';
import { Role } from '../api';
import { useUserContextSelector } from './useUserContext';

type RoleAccessType =
  | 'OrganizationManagementRoles'
  | 'ActivityAccessRoles'
  | 'EmployeeManagementRoles'
  | 'ChatAccessRoles'
  | 'LookupManagementRoles'
  | 'TemplateManagementRoles'
  | 'PatientListAccessRoles'
  | 'PatientManagementRoles'
  | 'PatientPortalAccessRoles'
  | 'PatientFinancialManagementRoles'
  | 'ScheduleManagementRoles'
  | 'EncounterManagementRoles'
  | 'DashboardViewRoles'
  | 'AdministrationRoles'
  | 'ScheduleViewAccessRoles'
  | 'ClinicalRoles'
  | 'EncounterReportAccessRoles';

const RoleAccessMap: Record<RoleAccessType, Role[]> = {
  OrganizationManagementRoles: ['GLOBAL_ADMIN', 'ADMIN'],
  ActivityAccessRoles: ['GLOBAL_ADMIN', 'SECURITY'],
  EmployeeManagementRoles: ['ADMIN'],
  ChatAccessRoles: ['ADMIN', 'PERFORMER', 'SECURITY', 'COORDINATOR', 'PATIENT', 'CARE_HOME_STAFF'],
  LookupManagementRoles: ['GLOBAL_ADMIN', 'ADMIN'],
  TemplateManagementRoles: ['GLOBAL_ADMIN', 'ADMIN'],
  PatientListAccessRoles: ['PERFORMER', 'COORDINATOR', 'CARE_HOME_STAFF'],
  PatientManagementRoles: ['PERFORMER', 'COORDINATOR', 'CARE_HOME_STAFF'],
  PatientPortalAccessRoles: ['PERFORMER', 'COORDINATOR'],
  PatientFinancialManagementRoles: [],
  ScheduleManagementRoles: ['PERFORMER', 'COORDINATOR'],
  EncounterManagementRoles: ['PERFORMER', 'COORDINATOR'],
  DashboardViewRoles: ['PERFORMER', 'COORDINATOR', 'PATIENT', 'CARE_HOME_STAFF'],
  AdministrationRoles: ['ADMIN', 'GLOBAL_ADMIN', 'SECURITY'],
  ScheduleViewAccessRoles: ['PERFORMER', 'COORDINATOR', 'ADMIN'],
  ClinicalRoles: ['PERFORMER'],
  EncounterReportAccessRoles: ['PERFORMER', 'COORDINATOR', 'CARE_HOME_STAFF'],
};

export const useRoleBasedAccess = (): Record<RoleAccessType, boolean> => {
  const roles = useUserContextSelector((x) => x.roles as Role[]);

  return useMemo(() => {
    const hasAccess = (requiredRoles: Role[]): boolean => requiredRoles.some((role) => roles.includes(role));

    return Object.keys(RoleAccessMap).reduce((acc, key) => {
      const roleKey = key as RoleAccessType;
      acc[roleKey] = hasAccess(RoleAccessMap[roleKey]);
      return acc;
    }, {} as Record<RoleAccessType, boolean>);
  }, [roles]);
};
